import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAuth } from './context/AuthContext';
import { AppContextProvider } from './context/AppContext';
import LoadingOverlay from './components/common/LoadingOverlay';
import AppHeader from './components/AppHeader';
import ImpersonationBanner from './components/ImpersonationBanner';
import UploadFile from './components/UploadFile';
import SelectColumns from './components/SelectColumns';
import ConfigureFilters from './components/ConfigureFilters';
import FilterResults from './components/FilterResults';
import ResearchAgentNewProject from './components/research/ResearchAgentNewProject';
import ResearchAgentActivityHistory from './components/research/ResearchAgentActivityHistory';
import SidebarNavigation from './components/common/SidebarNavigation';
import TicketDashboard from './components/TicketDashboard';
import TemplatesPage from './components/TemplatesPage';
import HomePage from './components/HomePage';
import UserProfile from './components/UserProfile';
import SubscriptionManager from './components/subscription/SubscriptionManager';
import BillingManager from './components/billing/BillingManager';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Dialog from '@mui/material/Dialog';
import { apiGet } from './utils/apiUtils';
import { createLogger, enableDebug } from './utils/debugUtils';

enableDebug(true);

const logger = createLogger('App');

const theme = createTheme({
  palette: {
    mode: 'dark',
    // Wyspur Brand Colors
    primary: {
      main: '#0095FF', // --wyspur-blue (kept/positive)
    },
    secondary: {
      main: '#E83E8C', // --wyspur-pink (excluded/negative)
    },
    success: {
      main: '#0095FF', // Changed from green to Wyspur blue
    },
    error: {
      main: '#E83E8C', // Changed from red to Wyspur pink
    },
    warning: {
      main: '#FFD700', // --wyspur-yellow
    },
    info: {
      main: '#AB47BC', // --wyspur-purple
    },
    background: {
      default: '#0D0D21', // --wyspur-navy
      paper: '#161c3e',   // --wyspur-surface
    },
    text: {
      primary: '#FFFFFF', // --wyspur-text
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    divider: 'rgba(0,0,0,0)', // Removed horizontal divider lines
    
    // Custom Wyspur palette
    wyspur: {
      navy: '#0D0D21',    // Main page background
      surface: '#161c3e', // Cards / tables 
      text: '#FFFFFF',    // Primary text
      blue: '#0095FF',    // Positive / "Kept"
      pink: '#E83E8C',    // Negative / "Excluded"
      yellow: '#faa32c',  // Icons / progress dots (NEW YELLOW)
      purple: '#de4097',  // Secondary accent (NEW PURPLE)
      border: '#3C3C5A',  // Table grid / outlines
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            backgroundColor: '#4361ee',
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          // Default step icon - using Wyspur border color
          color: '#3C3C5A', 
          '&.Mui-completed': {
            // NEW Wyspur purple for completed steps
            color: '#de4097',
          },
          '&.Mui-active': {
            // NEW Wyspur yellow for active step
            color: '#faa32c',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

const steps = ['Upload your list', 'Select the Columns you want to analyze', 'Create your qualification criteria', 'View results'];

function App() {
  const { isAuthenticated, user } = useAuth();
  
  const userId = user?.id || 'anonymous';
  const userStateKey = `filterToolState_${userId}`;
  
  // State to toggle between different application modes
  const [activeMode, setActiveMode] = useState('home');
  // Add state for tracking the active submenu item (kept for compatibility but not used)
  const [activeSubMode, setActiveSubMode] = useState(null);
  
  // Add state for profile dialog
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  // Add state for subscription dialog
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);

  // Add state for billing dialog
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);

  // State to trigger profile data reload in AppHeader
  const [profileUpdateTrigger, setProfileUpdateTrigger] = useState(0);
  
  // Function to trigger profile data reload in AppHeader
  const handleProfileUpdate = () => {
    setProfileUpdateTrigger(prev => prev + 1);
  };
  
  // Check URL on mount to detect hidden /tickets route
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes('/tickets')) {
      setActiveMode('tickets');
    }
  }, []);
  
  // Create ref for FilterResults component to access current overrides
  const filterResultsRef = useRef(null);
  
  const loadSavedState = () => {
    try {
      if (!isAuthenticated) {
        logger.info('User not authenticated, starting with fresh state');
        return null;
      }
      
      const savedState = localStorage.getItem(userStateKey);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        
        logger.debug('Found saved state in localStorage', { 
          key: userStateKey,
          savedUserId: parsedState.userId,
          currentUserId: userId,
          stateKeys: Object.keys(parsedState)
        });
        
        if (parsedState.userId === userId) {
          logger.info(`Loaded saved state for user: ${userId}`);
          
          // Log specific state items that might be causing issues
          if (parsedState.selectedColumns) {
            logger.debug('Found selectedColumns in saved state', {
              columns: parsedState.selectedColumns,
              count: parsedState.selectedColumns.length
            });
          }
          
          const now = Date.now();
          const stateAge = now - (parsedState.timestamp || 0);
          const maxAge = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
          
          if (stateAge > maxAge) {
            logger.info('Saved state is stale (older than 12 hours), starting fresh');
            return null;
          }
          
          return parsedState;
        } else {
          logger.warn('Found state for a different user, starting fresh');
          return null;
        }
      } else {
        logger.debug('No saved state found in localStorage');
      }
    } catch (error) {
      logger.error('Error loading saved state:', error);
    }
    return null;
  };

  const initialState = loadSavedState() || {
    activeStep: 0,
    fileData: {
      fileId: null,
      rowCount: 0,
      columnCount: 0,
      columns: []
    },
    selectedColumns: [],
    filterParams: {
      model: 'gpt-4o', // Default to gpt-4o as the recommended model
      reasoningText: '',
      globalInstructions: '',
      columnRules: {},
      logicMode: 'AND'
    },
    filterConfig: {},
    filterId: null,
    filterResults: null,
    userOverrides: {}
  };
  
  const [activeStep, setActiveStep] = useState(initialState.activeStep);
  
  const [fileData, setFileData] = useState(initialState.fileData);
  const [selectedColumns, setSelectedColumns] = useState(initialState.selectedColumns);
  const [filterConfig, setFilterConfig] = useState(initialState.filterConfig);
  const [filterParams, setFilterParams] = useState(initialState.filterParams);
  const [filterId, setFilterId] = useState(initialState.filterId);
  const [filterResults, setFilterResults] = useState(initialState.filterResults);
  const [userOverrides, setUserOverrides] = useState(initialState.userOverrides);
  const [jobName, setJobName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [filterProgress, setFilterProgress] = useState(null);
  
  // Debounced state saving using useRef to avoid creating new timeouts on every change
  const saveTimeoutRef = useRef(null);
  const stateRef = useRef({ activeStep, fileData, selectedColumns, filterConfig, filterParams, filterId, userOverrides, isAuthenticated, userId, userStateKey });
  
  // Update state ref on every render (lightweight)
  stateRef.current = { activeStep, fileData, selectedColumns, filterConfig, filterParams, filterId, userOverrides, isAuthenticated, userId, userStateKey };
  
  const saveStateToLocalStorageDebounced = useCallback(() => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const state = stateRef.current;
        if (!state.isAuthenticated) {
          return;
        }
        
        const stateToSave = {
          userId: state.userId,
          activeStep: state.activeStep,
          fileData: state.fileData,
          selectedColumns: state.selectedColumns,
          filterConfig: state.filterConfig,
          filterParams: state.filterParams,
          filterId: state.filterId,
          filterResults: null,
          userOverrides: state.userOverrides,
          timestamp: Date.now()
        };
        
        localStorage.setItem(state.userStateKey, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving state to localStorage:', error);
      }
    }, 500);
  }, []); // Empty dependency array - function never changes

  // Immediate save version for important state changes (step navigation, etc.)
  const saveStateToLocalStorage = useCallback(() => {
    try {
      if (!isAuthenticated) {
        return;
      }
      
      const stateToSave = {
        userId, // Include userId in the state object for verification
        activeStep,
        fileData,
        selectedColumns,
        filterConfig,
        filterParams,
        filterId,
        filterResults: null, // Don't save large result sets
        userOverrides,
        timestamp: Date.now() // Add timestamp for stale state detection
      };
      
      // Save with user-specific key to prevent cross-user contamination
      localStorage.setItem(userStateKey, JSON.stringify(stateToSave));
      // Removed heavy logging operations for performance
    } catch (error) {
      console.error('Error saving state to localStorage:', error);
    }
  }, [activeStep, fileData, selectedColumns, filterConfig, filterParams, filterId, userOverrides, isAuthenticated, userId, userStateKey]);
  
  // Save state when any relevant state changes (debounced for typing performance)
  useEffect(() => {
    // Use debounced save for better typing performance
    const cleanup = saveStateToLocalStorageDebounced();
    return cleanup;
  }, [saveStateToLocalStorageDebounced]);
  
  // Create a ref to track expanded state of UI elements
  const expandedStatesRef = React.useRef({});
  
  // Save expanded states function - but only if authenticated
  const saveExpandedStates = useCallback(() => {
    // Don't save expanded states if user is not authenticated
    if (!isAuthenticated) return;
    
    // This would collect expanded states from components if they expose them
    // Currently just placeholder for concept - actual implementation would
    // need components to expose their expanded states
    localStorage.setItem('filterToolExpandedStates', JSON.stringify(expandedStatesRef.current));
  }, [isAuthenticated]);
  
  // Reset state completely when authentication changes (user logs out)
  useEffect(() => {
    // If user is not authenticated, reset everything
    if (!isAuthenticated) {
      console.log('User logged out, resetting all filter tool state');
      
      // Reset all application state
      setActiveStep(0);
      setFileData({
        fileId: null,
        rowCount: 0,
        columnCount: 0,
        columns: []
      });
      setSelectedColumns([]);
      setFilterConfig({});
      setFilterParams({
        model: 'gpt-4o', // Always using gpt-4o as the default model
        reasoningText: '',
        chunkSize: 300,
        logicMode: 'AND'
      });
      setFilterId(null);
      setFilterResults(null);
      setFilterProgress(null);
      setUserOverrides({});
      
      // Clear all user-specific application localStorage data
      localStorage.removeItem(userStateKey);
      localStorage.removeItem(`filterToolExpandedStates_${userId}`);
    }
  }, [isAuthenticated, userId, userStateKey]);

  // Set up event listeners for tab/window visibility changes with DOM and UI state preservation
  useEffect(() => {
    // Track if we need to prevent the next visibility change from causing re-renders
    let preventNextRefresh = false;
    
    // Create an empty event handler that doesn't change state at all
    // This completely prevents React from re-rendering due to visibility events
    const handleVisibilityChange = (e) => {
      // Just store state without triggering renders
      if (document.visibilityState === 'hidden') {
        // When leaving tab, save everything but don't modify state variables
        saveStateToLocalStorage();
        saveExpandedStates();
        preventNextRefresh = true;
      } else if (preventNextRefresh) {
        // When returning to tab, only restore if needed, but avoid state changes
        preventNextRefresh = false;
        // Block any auth checks or refreshes for 1 second after tab return
        e.stopPropagation();
      }
    };

    // Add event listener with capture to intercept before React sees it
    document.addEventListener('visibilitychange', handleVisibilityChange, { capture: true });

    // Handle focus/blur the same way - they often cause unwanted refreshes
    const handleFocusEvents = (e) => {
      e.stopPropagation(); // Stop event from bubbling to React
      if (e.type === 'blur') {
        saveStateToLocalStorage();
        saveExpandedStates();
      }
    };
    
    // Capture these events before React sees them - capture: true is critical
    window.addEventListener('focus', handleFocusEvents, { capture: true });
    window.addEventListener('blur', handleFocusEvents, { capture: true });
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, { capture: true });
      window.removeEventListener('focus', handleFocusEvents, { capture: true });
      window.removeEventListener('blur', handleFocusEvents, { capture: true });
    };
  }, [saveStateToLocalStorage, saveExpandedStates]);

  // Functions to control the step workflow
  const handleNext = () => {
    // Reduced logging for better performance
    // logger.info(`Moving to next step from step ${activeStep}`);
    
    // Simple step progression - templates now just pre-fill forms, no complex state tracking needed
    
    // After processing, log the final selected columns state
    logger.debug('Final selected columns after processing', {
      selectedColumns,
      count: selectedColumns.length
    });
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    logger.info(`Moving back from step ${activeStep}`);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    logger.info('Full application reset initiated');
    
    // Clear state in memory
    setActiveStep(0);
    setFileData({
      fileId: null,
      rowCount: 0,
      columnCount: 0,
      columns: []
    });
    setSelectedColumns([]);
    setFilterConfig({});
    setFilterParams({
      model: 'gpt-4o', // Updated default model to match initial state
      reasoningText: '',
      chunkSize: 300,
      logicMode: 'AND'
    });
    setFilterId(null);
    setFilterResults(null);
    setFilterProgress(null);
    setUserOverrides({});
    setJobName(''); // Clear job name
    
    // Clear ALL relevant localStorage items that could cause issues
    try {
      logger.debug('Clearing all localStorage items that could affect the application');
      
      // Main application state
      localStorage.removeItem('filterToolState');
      localStorage.removeItem(userStateKey); // Clear user-specific state too
      
      // Template-related cleanup
      localStorage.removeItem('templateOverrides');
      localStorage.removeItem('templateId');
      
      logger.info('All localStorage items cleared successfully');
    } catch (error) {
      logger.error('Error clearing localStorage items:', error);
    }
  };

  // For debugging, watch selectedColumns changes (disabled for performance)
  // const prevSelectedColumnsRef = useRef(selectedColumns);
  // useEffect(() => {
  //   const prevColumns = prevSelectedColumnsRef.current;
  //   if (JSON.stringify(prevColumns) !== JSON.stringify(selectedColumns)) {
  //     logger.info('selectedColumns state changed', {
  //       prev: prevColumns,
  //       current: selectedColumns,
  //       added: selectedColumns.filter(col => !prevColumns.includes(col)),
  //       removed: prevColumns.filter(col => !selectedColumns.includes(col))
  //     });
  //     prevSelectedColumnsRef.current = selectedColumns;
  //   }
  // }, [selectedColumns]);
  
  // Render the appropriate component based on the active step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <UploadFile 
            onNext={handleNext} 
            setLoading={setLoading} 
            setFileData={setFileData}
            setSelectedColumns={setSelectedColumns}
            setFilterParams={setFilterParams}
            setFilterConfig={setFilterConfig}
            setJobName={setJobName}
          />
        );
      case 1:
        return (
          <SelectColumns 
            onNext={handleNext} 
            onBack={handleBack} 
            setLoading={setLoading} 
            fileData={fileData}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            setFileData={setFileData}
            setFilterParams={setFilterParams}
            setFilterConfig={setFilterConfig}
          />
        );
      case 2:
        return (
          <ConfigureFilters 
            onNext={handleNext} 
            onBack={handleBack} 
            setLoading={setLoading} 
            fileData={fileData}
            selectedColumns={selectedColumns}
            filterConfig={filterConfig}
            setFilterConfig={setFilterConfig}
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            setFilterId={setFilterId}
            jobName={jobName}
            onSuccess={() => {
              // When filter job creation is successful, clear all data and switch to activity history
              handleReset();
              setActiveMode('activity-history');
              setActiveSubMode('list-cleaner'); // Signal to show List Cleaner tab
            }}
          />
        );
      case 3:
        return (
          <FilterResults 
            ref={filterResultsRef}
            onReset={handleReset} 
            onBack={handleBack}
            setLoading={setLoading} 
            filterId={filterId}
            filterResults={filterResults}
            setFilterResults={setFilterResults}
            userOverrides={userOverrides}
            setUserOverrides={setUserOverrides}
            filterProgress={filterProgress}
            setFilterProgress={setFilterProgress}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  // Effect to validate filterID on startup and whenever it changes
  // This is critical to ensure we don't get stuck with an invalid filterId
  useEffect(() => {
    const validateFilterId = async () => {
      // Only validate if we have a filterId but no results yet
      if (filterId && !filterResults) {
        try {
          console.log(`Validating filter ID: ${filterId}`);
      // Check if the filter ID actually exists on the server - using authenticated fetch
      const response = await apiGet(`/api/progress/${filterId}`);
      console.log(`[PROGRESS-DEBUG] Progress check response status: ${response.status} for ${filterId}`);
          
          if (!response.ok) {
            console.warn(`Invalid filter ID detected (${filterId}), clearing state`);
            // If server returns 404 or any error, this means the filter ID is no longer valid
            setFilterId(null);
            setFilterProgress(null);
            setLoading(false);
            
            // If we're on the results step, go back to configuration
            if (activeStep === 3) {
              setActiveStep(2);
              alert('Previous filter process was not found. Please configure your filters again.');
            }
            return false;
          }
          
          const progressData = await response.json();
          if (progressData.status === 'not_found') {
            console.warn(`Filter ID not found on server: ${filterId}`);
            // Server explicitly says this filter ID doesn't exist
            setFilterId(null);
            setFilterProgress(null);
            setLoading(false);
            
            // If we're on the results step, go back to configuration
            if (activeStep === 3) {
              setActiveStep(2);
              alert('Previous filter process was not found. Please configure your filters again.');
            }
            return false;
          }
          
          return true; // Filter ID is valid
        } catch (error) {
          console.error('Error validating filter ID:', error);
          // On error, consider the filter ID invalid
          setFilterId(null);
          setFilterProgress(null);
          setLoading(false);
          return false;
        }
      }
      return !!filterId; // Return true if we have a filterId (and didn't need validation), false otherwise
    };
    
    // Run validation once when the component mounts or filterId changes
    validateFilterId();
  }, [filterId, filterResults, activeStep, setActiveStep]);

  // Effect to poll for filter progress if we have a filterId but no results yet
  useEffect(() => {
    let intervalId;
    
    if (filterId && !filterResults && activeStep === 3) {
      // Start polling for progress
      setLoading(true);
      
      intervalId = setInterval(async () => {
        try {
          const response = await apiGet(`/api/progress/${filterId}`);
          console.log(`[PROGRESS-DEBUG] Polling progress check status: ${response.status} for ${filterId}`);
          
          // Handle case where server returns error (filter job disappeared)
          if (!response.ok) {
            console.warn(`Filter job ${filterId} no longer exists on server`);
            clearInterval(intervalId);
            setFilterId(null);
            setFilterProgress(null);
            setLoading(false);
            return;
          }
          
          const progressData = await response.json();
          setFilterProgress(progressData);
          
          // Handle case where server explicitly says job not found
          if (progressData.status === 'not_found') {
            console.warn(`Filter job ${filterId} not found on server`);
            clearInterval(intervalId);
            setFilterId(null);
            setFilterProgress(null);
            setLoading(false);
            return;
          }
          
          // If completed, stop polling and get results
          if (progressData.status === 'completed') {
            clearInterval(intervalId);
            
            // Fetch the complete results
            const resultsResponse = await apiGet(`/api/results/${filterId}`);
            const resultsData = await resultsResponse.json();
            
            setFilterResults(resultsData);
            setLoading(false);
          } else if (progressData.status === 'failed') {
            // If failed, stop polling
            clearInterval(intervalId);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
          // On any error, consider the polling failed and stop
          clearInterval(intervalId);
          setLoading(false);
        }
      }, 1000); // Poll every second
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [filterId, filterResults, activeStep]);

  // Debug logging for application state
  console.log('App rendering with state:', {
    activeMode,
    activeStep,
    loading,
    filterProgress,
    filterResults: filterResults ? filterResults.length : 0
  });
  
  // App context value with all the state and functions we want to share

  // Return the main app structure without router
  // Create app context value with all the state and functions we want to share
  const appContextValue = {
    activeStep,
    setActiveStep,
    fileData,
    setFileData,
    selectedColumns,
    setSelectedColumns,
    filterConfig,
    setFilterConfig,
    filterParams,
    setFilterParams,
    filterId,
    setFilterId,
    filterResults,
    setFilterResults,
    userOverrides,
    setUserOverrides,
    loading,
    setLoading,
    handleReset  // Using the existing handleReset function
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContextProvider value={appContextValue}>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          {/* Sidebar Navigation with submenu support */}
          <SidebarNavigation
            activeMode={activeMode}
            onModeChange={setActiveMode}
            activeSubMode={activeSubMode}
            onSubModeChange={setActiveSubMode}
          />
          
          {/* Main Content */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column'
          }}>
            <AppHeader
              onOpenProfile={() => setProfileDialogOpen(true)}
              onOpenSubscription={() => setSubscriptionDialogOpen(true)}
              onOpenBilling={() => setBillingDialogOpen(true)}
              profileUpdateTrigger={profileUpdateTrigger}
            />

            {/* Impersonation Banner - shows when admin is impersonating a user */}
            <ImpersonationBanner />

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Only show loading overlay for filter tool mode */}
              {activeMode === 'filter' && (
                <LoadingOverlay 
                  isLoading={loading} 
                  progress={filterProgress} 
                  title={activeStep === 0 ? "Upload Processing" : "Filter Processing"} 
                />
              )}
              
              {/* Conditionally render Home, Research Agent, Filter Tool, or Tickets Dashboard */}
              {activeMode === 'home' ? (
                /* Home Page Dashboard */
                <HomePage onNavigate={setActiveMode} />
              ) : activeMode === 'tickets' ? (
                /* Hidden Tickets Dashboard */
                <Container maxWidth={false} sx={{ mt: 4, mb: 4, flex: 1, ml: 2, mr: 2 }}>
                  <TicketDashboard />
                </Container>
              ) : activeMode === 'activity-history' ? (
                /* Activity History - shows both Research and List Cleaner jobs */
                <Container maxWidth={false} sx={{ mt: 4, mb: 4, flex: 1, ml: 2, mr: 2 }}>
                  <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                    <ResearchAgentActivityHistory 
                      initialTab={activeSubMode === 'list-cleaner' ? 1 : 0}
                      onTabChange={() => setActiveSubMode(null)}
                    />
                  </Paper>
                </Container>
              ) : activeMode === 'research' ? (
                /* Research Agent - Create New Project */
                <Container maxWidth={false} sx={{ mt: 4, mb: 4, flex: 1, ml: 2, mr: 2 }}>
                  <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                    <ResearchAgentNewProject 
                      onSuccess={() => {
                        // When job creation is successful, switch to activity history
                        setActiveMode('activity-history');
                      }}
                    />
                  </Paper>
                </Container>
              ) : activeMode === 'templates' ? (
                /* Templates Page - Unified List Cleaner and Research Templates */
                <Container maxWidth={false} sx={{ mt: 4, mb: 4, flex: 1, ml: 2, mr: 2 }}>
                  <Paper elevation={3} sx={{ p: 0 }}>
                    <TemplatesPage onModeChange={setActiveMode} />
                  </Paper>
                </Container>
              ) : (
                /* Filter Tool Mode */
                <Container maxWidth={false} sx={{ mt: 4, mb: 4, flex: 1, ml: 2, mr: 2 }}>
                  <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                    {/* Add step title for each step */}
                    <Box sx={{ mb: 3 }}>
                      <Typography 
                        variant="h6" 
                        component="h1" 
                        gutterBottom
                        sx={{ 
                          textTransform: 'uppercase',
                          letterSpacing: '0.15em',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          color: '#FFFFFF'
                        }}
                      >
                        Step {activeStep + 1}: {steps[activeStep]}
                      </Typography>
                    </Box>
                    
                    {getStepContent(activeStep)}
                  </Paper>
                </Container>
              )}
            </Box>
          </Box>
        </Box>
        
        {/* Profile Dialog */}
        <Dialog
          open={profileDialogOpen}
          onClose={() => setProfileDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.default',
              color: 'text.primary'
            }
          }}
        >
          <UserProfile
            onClose={() => setProfileDialogOpen(false)}
            onProfileUpdate={handleProfileUpdate}
          />
        </Dialog>

        {/* Subscription Dialog */}
        <Dialog
          open={subscriptionDialogOpen}
          onClose={() => setSubscriptionDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.default',
              color: 'text.primary'
            }
          }}
        >
          <SubscriptionManager />
        </Dialog>

        {/* Billing Dialog */}
        <Dialog
          open={billingDialogOpen}
          onClose={() => setBillingDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.default',
              color: 'text.primary'
            }
          }}
        >
          <BillingManager
            onClose={() => setBillingDialogOpen(false)}
            onOpenSubscription={() => {
              setBillingDialogOpen(false);
              setSubscriptionDialogOpen(true);
            }}
          />
        </Dialog>

      </AppContextProvider>
    </ThemeProvider>
  );
}

export default App;
